import { useMemo } from "react";
import { useCompany } from "./useCompany";
import { useCurrentUser } from "./useCurrentUser";
import {
  useGetCardPreferencesQuery,
  useCreateCardPreferencesMutation,
  useUpdateCardPreferencesMutation,
} from "../services/company.service";
import {
  useGetAdminCardPreferencesQuery,
  useCreateAdminCardPreferencesMutation,
  useUpdateAdminCardPreferencesMutation,
} from "../services/admin.service";
import {
  CardPreferenceType,
  CardPreferencePage,
  CARD_PREFERENCE_TYPE,
} from "../constants/cardPreferences";
import { ROLE_ADMIN_ID } from "../configs/api";

interface UseCardPreferencesOptions {
  type: CardPreferenceType;
  page: CardPreferencePage;
  skip?: boolean;
}

export const useCardPreferences = ({
  type,
  page,
  skip = false,
}: UseCardPreferencesOptions) => {
  const company = useCompany();
  const companyId = company?.id;
  const user = useCurrentUser();
  const isAdmin = user?.role_id === ROLE_ADMIN_ID;
  const isAdminType = type === CARD_PREFERENCE_TYPE.ADMIN;

  // Admin API (no companyId needed)
  const {
    data: adminPreferences,
    isLoading: isAdminLoading,
    isFetching: isAdminFetching,
    error: adminError,
    refetch: refetchAdmin,
  } = useGetAdminCardPreferencesQuery(page, {
    skip: skip || !isAdminType, // Only check isAdminType, not isAdmin (type is determined by props)
  });

  // Customer API (requires companyId)
  const {
    data: customerPreferences,
    isLoading: isCustomerLoading,
    isFetching: isCustomerFetching,
    error: customerError,
    refetch: refetchCustomer,
  } = useGetCardPreferencesQuery(
    {
      companyId: companyId!,
      type,
      page,
    },
    {
      skip: skip || isAdminType || !companyId,
    }
  );

  const [createAdminPreferences, { isLoading: isCreatingAdmin }] =
    useCreateAdminCardPreferencesMutation();
  const [updateAdminPreferences, { isLoading: isUpdatingAdmin }] =
    useUpdateAdminCardPreferencesMutation();
  const [createCustomerPreferences, { isLoading: isCreatingCustomer }] =
    useCreateCardPreferencesMutation();
  const [updateCustomerPreferences, { isLoading: isUpdatingCustomer }] =
    useUpdateCardPreferencesMutation();

  // Use admin or customer preferences based on type
  const preferences = isAdminType ? adminPreferences : customerPreferences;
  const isLoading = isAdminType ? isAdminLoading : isCustomerLoading;
  const isFetching = isAdminType ? isAdminFetching : isCustomerFetching;
  const error = isAdminType ? adminError : customerError;
  const refetch = isAdminType ? refetchAdmin : refetchCustomer;

  const isEmpty = useMemo(() => {
    return !preferences || preferences.cardOrder.length === 0;
  }, [preferences]);

  const savePreferences = async (
    cardOrder: string[],
    hiddenCards?: string[],
    details?: Record<string, any>
  ) => {
    if (isAdminType) {
      // Admin: no companyId needed, but support userId for per-user preferences
      const userId = user?.id;
      
      if (isEmpty) {
        return await createAdminPreferences({
          page,
          cardOrder,
          hiddenCards,
          details,
          userId, // Send userId to create per-user preference
        }).unwrap();
      } else {
        return await updateAdminPreferences({
          page,
          cardOrder,
          hiddenCards,
          details,
          userId, // Send userId to update per-user preference
        }).unwrap();
      }
    } else {
      // Customer: requires companyId
      if (!companyId) {
        throw new Error("Company ID is required for customer preferences");
      }

      // Get userId from current user for per-user preferences
      const userId = user?.id;

      if (isEmpty) {
        return await createCustomerPreferences({
          companyId,
          type,
          page,
          cardOrder,
          hiddenCards,
          details,
          userId, // Send userId to create per-user preference
        }).unwrap();
      } else {
        return await updateCustomerPreferences({
          companyId,
          type,
          page,
          cardOrder,
          hiddenCards,
          details,
          userId, // Send userId to update per-user preference
        }).unwrap();
      }
    }
  };

  return {
    preferences,
    isLoading: isLoading || isFetching,
    isSaving: isAdminType
      ? isCreatingAdmin || isUpdatingAdmin
      : isCreatingCustomer || isUpdatingCustomer,
    error,
    isEmpty,
    savePreferences,
    refetch,
    companyId,
  };
};
